import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AgentLayout from './AgentLayout';
import Icon from '../../components/AppIcon';
import { selectCurrentUser } from '../../features/auth/authSlice';
import { useCurrency } from '../../features/currency/CurrencyContext';
import {
  useGetSubscriptionPackagesQuery,
  useStartSubscriptionPaymentMutation,
  useStartSubscriptionTrialMutation,
  useLazyGetSubscriptionPaymentStatusQuery,
} from '../../features/api/apiSlice';

const METHOD_COPY = {
  flutterwave: {
    title: 'Card or mobile money',
    hint: 'Pay on a secure Flutterwave page. Supports cards, MTN, Airtel, and more.',
    icon: 'CreditCard',
  },
  mtn_momo: {
    title: 'MTN Mobile Money',
    hint: 'We send a prompt to your MTN phone. Enter your PIN to finish.',
    icon: 'Smartphone',
  },
  airtel_momo: {
    title: 'Airtel Money',
    hint: 'We send a prompt to your Airtel phone. Enter your PIN to finish.',
    icon: 'Smartphone',
  },
};

const AgentSubscription = () => {
  const user = useSelector(selectCurrentUser);
  const { formatPrice } = useCurrency();
  const [params] = useSearchParams();
  const { data, isLoading, isError, refetch } = useGetSubscriptionPackagesQuery();
  const [startPay, { isLoading: paying }] = useStartSubscriptionPaymentMutation();
  const [startTrial, { isLoading: trialing }] = useStartSubscriptionTrialMutation();
  const [checkStatus] = useLazyGetSubscriptionPaymentStatusQuery();

  const [selectedId, setSelectedId] = useState(null);
  const [method, setMethod] = useState('flutterwave');
  const [phone, setPhone] = useState(user?.mobile || user?.phone || '');
  const [message, setMessage] = useState(null);
  const [polling, setPolling] = useState(false);

  const packages = data?.packages || [];
  const methods = data?.methods || [];
  const current = data?.current;
  const enabledMethods = methods.filter((m) => m.enabled);
  const selected = packages.find((p) => Number(p.id) === Number(selectedId)) || packages[0];

  useEffect(() => {
    if (!selectedId && packages.length) {
      const active = packages.find((p) => p.is_active);
      setSelectedId((active || packages[0]).id);
    }
  }, [packages, selectedId]);

  useEffect(() => {
    if (enabledMethods.length && !enabledMethods.some((m) => m.id === method)) {
      setMethod(enabledMethods[0].id);
    }
  }, [enabledMethods, method]);

  useEffect(() => {
    const status = params.get('status');
    if (status === 'success') {
      setMessage({ tone: 'ok', text: 'Payment received. Your subscription is now active.' });
      refetch();
    } else if (status === 'cancelled') {
      setMessage({ tone: 'warn', text: 'Payment was cancelled. You can try again.' });
    } else if (status === 'failed') {
      setMessage({ tone: 'err', text: 'Payment did not go through. Try another method.' });
    }
  }, [params, refetch]);

  const needsPhone = useMemo(
    () => enabledMethods.find((m) => m.id === method)?.needs_phone,
    [enabledMethods, method]
  );

  const limitLabel = (value) => {
    if (value === 'unlimited') return 'Unlimited';
    if (value === 'not_available') return 'Not included';
    return String(value);
  };

  const pollUntilPaid = async (txRef) => {
    setPolling(true);
    const started = Date.now();
    while (Date.now() - started < 120_000) {
      try {
        const res = await checkStatus(txRef).unwrap();
        if (res?.paid) {
          setPolling(false);
          setMessage({ tone: 'ok', text: res.message || 'Payment received. Your plan is active.' });
          refetch();
          return;
        }
        if (res?.status === 'failed') {
          setPolling(false);
          setMessage({ tone: 'err', text: res.message || 'Payment was not approved.' });
          return;
        }
      } catch {
        /* keep polling */
      }
      await new Promise((r) => setTimeout(r, 2500));
    }
    setPolling(false);
    setMessage({
      tone: 'warn',
      text: 'Still waiting for the phone approval. Approve it, then refresh this page.',
    });
  };

  const onPay = async () => {
    if (!selected) return;
    setMessage(null);
    if (needsPhone && !String(phone).replace(/\D/g, '').length) {
      setMessage({ tone: 'err', text: 'Enter the mobile money number that will receive the prompt.' });
      return;
    }
    try {
      const res = await startPay({
        package_id: selected.id,
        method,
        phone,
        return_url: `${window.location.origin}/agent-dashboard/subscription`,
      }).unwrap();
      if (res.activated) {
        setMessage({ tone: 'ok', text: res.message || 'Plan activated.' });
        refetch();
        return;
      }
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      if (res.poll && res.tx_ref) {
        setMessage({ tone: 'ok', text: res.instructions || res.message || 'Approve the prompt on your phone.' });
        pollUntilPaid(res.tx_ref);
        return;
      }
      setMessage({ tone: 'warn', text: res.message || 'Payment started.' });
    } catch (err) {
      setMessage({
        tone: 'err',
        text: err?.data?.message || err?.error || 'Could not start payment.',
      });
    }
  };

  const onTrial = async () => {
    if (!selected) return;
    setMessage(null);
    try {
      const res = await startTrial({ package_id: selected.id }).unwrap();
      setMessage({ tone: 'ok', text: res.message || 'Trial started.' });
      refetch();
    } catch (err) {
      setMessage({
        tone: 'err',
        text: err?.data?.message || 'Could not start the trial.',
      });
    }
  };

  return (
    <AgentLayout
      title="Subscription"
      subtitle="Pick a plan, then pay with Flutterwave, MTN, or Airtel Money. Most agents finish in under a minute."
    >
      {message && (
        <div
          className={`mb-5 rounded-xl border px-4 py-3 text-sm ${
            message.tone === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : message.tone === 'warn'
                ? 'border-amber-200 bg-amber-50 text-amber-900'
                : 'border-rose-200 bg-rose-50 text-rose-900'
          }`}
        >
          {message.text}
        </div>
      )}

      {current && (
        <div className="mb-6 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 flex items-start gap-3">
          <Icon name="Crown" size={20} className="text-violet-600 mt-0.5" />
          <div>
            <p className="font-semibold text-violet-900 text-sm">
              Current plan: {current.package_name || 'Active'}
              {current.is_trial ? ' (free trial)' : ''}
            </p>
            <p className="text-xs text-violet-800/80">
              {current.start_date ? `Started ${current.start_date}` : ''}
              {current.end_date ? ` · ends ${current.end_date}` : ' · no end date'}
            </p>
          </div>
        </div>
      )}

      {isLoading && <p className="text-sm text-slate-500">Loading plans…</p>}
      {isError && (
        <p className="text-sm text-rose-600">Could not load plans because there are no plans set yet.</p>
      )}

      {!isLoading && !packages.length && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
          No live plans yet.
        </div>
      )}

      <div className="grid lg:grid-cols-[1.4fr_.9fr] gap-6 items-start">
        <div className="grid sm:grid-cols-2 gap-4">
          {packages.map((pkg) => {
            const active = Number(pkg.id) === Number(selected?.id);
            return (
              <button
                key={pkg.id}
                type="button"
                onClick={() => setSelectedId(pkg.id)}
                className={`text-left rounded-2xl border p-5 transition-shadow ${
                  active
                    ? 'border-amber-400 ring-2 ring-amber-200 bg-white shadow-md'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-slate-900">{pkg.name}</h3>
                  {pkg.is_active && (
                    <span className="text-[10px] uppercase tracking-wide font-semibold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                      Yours
                    </span>
                  )}
                </div>
                <p className="text-2xl font-bold text-slate-900">
                  {pkg.is_free ? 'Free' : formatPrice(pkg.price, 'UGX')}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {pkg.duration ? `${pkg.duration} days` : 'No end date'}
                  {pkg.trial_days > 0 ? ` · ${pkg.trial_days}-day trial` : ''}
                </p>
                {pkg.description ? (
                  <p className="text-sm text-slate-600 mt-3">{pkg.description}</p>
                ) : null}
                <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                  <li>Listings: {limitLabel(pkg.property_limit)}</li>
                  <li>Promotions: {limitLabel(pkg.advertisement_limit)}</li>
                  {pkg.includes_verified_badge && <li>Verified-agent badge included</li>}
                  {pkg.includes_featured_placement && <li>Featured placement included</li>}
                </ul>
              </button>
            );
          })}
        </div>

        {selected && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 lg:sticky lg:top-24">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 mb-1">Checkout</p>
            <h3 className="font-semibold text-slate-900 mb-1">Pay for {selected.name}</h3>
            <p className="text-sm text-slate-500 mb-4">
              {selected.is_free
                ? 'This plan is free. Activate it with one click.'
                : `You will pay ${formatPrice(selected.price, 'UGX')}.`}
            </p>

            {selected.trial_available && selected.trial_days > 0 && (
              <button
                type="button"
                disabled={trialing}
                onClick={onTrial}
                className="mb-4 w-full inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-900 hover:bg-emerald-100 disabled:opacity-60"
              >
                <Icon name="Gift" size={16} />
                {trialing ? 'Starting trial…' : `Start ${selected.trial_days}-day free trial`}
              </button>
            )}

            {!selected.is_free && (
              <>
                <div className="space-y-2 mb-4">
                  {(enabledMethods.length ? enabledMethods : methods).map((m) => {
                    const copy = METHOD_COPY[m.id] || { title: m.label, hint: '', icon: 'Wallet' };
                    const disabled = !m.enabled;
                    return (
                      <label
                        key={m.id}
                        className={`flex items-start gap-3 rounded-xl border px-3 py-3 cursor-pointer ${
                          method === m.id ? 'border-amber-400 bg-amber-50/60' : 'border-slate-200'
                        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <input
                          type="radio"
                          name="pay-method"
                          className="mt-1"
                          disabled={disabled}
                          checked={method === m.id}
                          onChange={() => setMethod(m.id)}
                        />
                        <Icon name={copy.icon} size={18} className="text-slate-700 mt-0.5" />
                        <span>
                          <span className="block text-sm font-semibold text-slate-900">{copy.title}</span>
                          <span className="block text-xs text-slate-500">
                            {disabled ? 'Not configured yet. Ask admin to add API keys.' : copy.hint}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>

                {needsPhone && (
                  <label className="block mb-4">
                    <span className="text-sm font-medium text-slate-800">Mobile money number</span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="0770 000 000"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                    <span className="block text-xs text-slate-500 mt-1">
                      Use the number that will receive the approve prompt.
                    </span>
                  </label>
                )}

                <button
                  type="button"
                  onClick={onPay}
                  disabled={paying || polling || !enabledMethods.length}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-5 py-3 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
                >
                  <Icon name="Lock" size={16} />
                  {paying || polling
                    ? polling
                      ? 'Waiting for phone approval…'
                      : 'Starting payment…'
                    : `Pay ${formatPrice(selected.price, 'UGX')}`}
                </button>
                {!enabledMethods.length && (
                  <p className="text-xs text-rose-600 mt-2">
                    No payment method is enabled. Turn on Flutterwave, MTN, or Airtel in admin payment settings.
                  </p>
                )}
              </>
            )}

            {selected.is_free && (
              <button
                type="button"
                onClick={onPay}
                disabled={paying}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-5 py-3 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
              >
                {paying ? 'Activating…' : 'Activate free plan'}
              </button>
            )}
          </div>
        )}
      </div>
    </AgentLayout>
  );
};

export default AgentSubscription;
